import {
  Block,
  BlockTitle,
  Button,
  Icon,
  List,
  ListItem,
  Page,
  Popup,
  Segmented,
} from "framework7-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DeleteButton from "../components/delete-button";
import PopupNavbar from "../components/popup-navbar";
import { requests } from "../constants/requests";
import { usePopupBackClose } from "../hooks/use-back-close";
import { useDeleteEntry } from "../hooks/use-delete-entry";
import { useEnterKeys } from "../hooks/use-enter-keys";
import { vibrate } from "../hooks/use-haptics";
import { useIsPending } from "../hooks/use-is-pending";
import { useToast } from "../hooks/use-toast";
import {
  createExpense,
  selectGroupTags,
  selectMe,
  updateExpense,
} from "../redux/reducer/app-slice";
import {
  EXPENSE_TYPE,
  NOTE_MAX_LENGTH,
  defaultPayerId,
  descriptionChange,
  isIncome,
  noteLength,
  splitOfExpense,
  tagNamesOf,
  toExpensePayload,
} from "../utils/expense";
import { initialSpentAtInput, resolveSpentAt } from "../utils/spent-at-form";
import { tagKey } from "../utils/tags";
import { queuedText } from "../utils/outbox";
import { countLabel } from "../utils/format";
import {
  SHARE_MODES,
  getRemainingCents,
  resplitForTotal,
  splitProblem,
} from "../utils/money";
import MoneyInput from "./money-input/money-input";
import NoteInput, { NOTE_INPUT_NAME } from "./note-input";
import PersonPickerPopup from "./person-picker-popup";
import ReceiversPopup, {
  MODE_LABELS,
  allReceivers,
  isEveryone,
} from "./receivers-popup";
import SpentAtInput from "./spent-at-input";
import TagPicker from "./tags/tag-picker";

const initialPayer = (expense, participants, me) => {
  if (expense?.id) return expense.payer ?? "";
  return expense?.payer ?? defaultPayerId(me, participants) ?? "";
};

const entryTypeOf = (expense) =>
  isIncome(expense) ? EXPENSE_TYPE.income : EXPENSE_TYPE.expense;

const getInitialValues = (expense, participants, me) => {
  const amount = Number(expense?.amount_in_decimals) || 0;
  return {
    type: entryTypeOf(expense),
    name: expense?.name || "",
    amount,
    payer: initialPayer(expense, participants, me),
    split: expense?.id
      ? splitOfExpense(expense)
      : {
          mode: SHARE_MODES.equal,
          receivers: allReceivers(amount, participants),
        },
    tags: tagNamesOf(expense),
    spentAt: initialSpentAtInput(expense),
    note: expense?.description ?? "",
  };
};

const sameTags = (a, b) =>
  a.length === b.length && a.every((name, i) => tagKey(name) === tagKey(b[i]));

const getReceiversLabel = ({ mode, receivers }, participants) => {
  if (receivers.length === 0) return "Empfänger auswählen";
  const people = isEveryone(receivers, participants)
    ? `Alle (${participants.length})`
    : countLabel(receivers.length, "Person", "Personen");
  return mode === SHARE_MODES.weights || mode === SHARE_MODES.percent
    ? `${people} · ${MODE_LABELS[mode]}`
    : people;
};

const NOTE_TOO_LONG = `Die Notiz darf höchstens ${NOTE_MAX_LENGTH} Zeichen haben.`;

const TYPE_OPTIONS = [
  { type: EXPENSE_TYPE.expense, label: "Ausgabe" },
  { type: EXPENSE_TYPE.income, label: "Einnahme" },
];

const WORDING = {
  [EXPENSE_TYPE.expense]: {
    newTitle: null,
    editTitle: null,
    payerLabel: "Von",
    payerPlaceholder: "Zahler auswählen",
    payerPickerTitle: "Zahler auswählen",
    payerMissing: "Zahler muss ausgewählt werden!",
    receiversTitle: "Für wen wurde bezahlt?",
    saved: "Ausgabe gespeichert",
    deleteLabel: "Ausgabe löschen",
  },
  [EXPENSE_TYPE.income]: {
    newTitle: "Einnahme erfassen",
    editTitle: "Einnahme bearbeiten",
    namePlaceholder: "z. B. Pfand",
    payerLabel: "Erhalten von",
    payerPlaceholder: "Person auswählen",
    payerPickerTitle: "Wer hat das Geld erhalten?",
    payerMissing: "Wähle aus, wer das Geld erhalten hat.",
    receiversTitle: "Wem gehört das Geld?",
    saved: "Einnahme gespeichert",
    deleteLabel: "Einnahme löschen",
  },
};

const validate = ({ title, amount, payerParticipant, split, note, wording }) => {
  if (!title) return "Name darf nicht leer sein!";
  if (!amount) return "Betrag muss größer als 0 sein!";
  if (!payerParticipant) return wording.payerMissing;
  if (split.receivers.length === 0) {
    return "Es muss mindestens einen Empfänger geben!";
  }
  const problem = splitProblem(split);
  if (problem) return problem;
  if (getRemainingCents(amount, split.receivers) !== 0) {
    return "Die Anteile ergeben nicht den Gesamtbetrag.";
  }
  if (noteLength(note.trim()) > NOTE_MAX_LENGTH) return NOTE_TOO_LONG;
  return null;
};

// onClose() only once closed, so the content does not flip during the close animation.
const ExpensePopup = ({
  autoFocus = false,
  expense,
  opened,
  onClose,
  participants,
}) => {
  const me = useSelector(selectMe);
  const [initialValues] = useState(() =>
    getInitialValues(expense, participants, me),
  );
  const [inputType, setInputType] = useState(initialValues.type);
  const [inputName, setInputName] = useState(initialValues.name);
  const [inputAmount, setInputAmount] = useState(initialValues.amount);
  const [payer, setPayer] = useState(initialValues.payer);
  const [inputSplit, setInputSplit] = useState(initialValues.split);
  const [inputTags, setInputTags] = useState(initialValues.tags);
  const [inputSpentAt, setInputSpentAt] = useState(initialValues.spentAt);
  const [inputNote, setInputNote] = useState(initialValues.note);
  const initialSpentAtRef = useRef(inputSpentAt);
  const [payerPopupOpened, setPayerPopupOpened] = useState(false);
  const [receiversPopupOpened, setReceiversPopupOpened] = useState(false);

  const popupRef = useRef(null);
  const nameInputRef = useRef(null);
  // Also blocks a save during the close animation (request state is done).
  const saveSentRef = useRef(false);

  const dispatch = useDispatch();
  const groupTags = useSelector(selectGroupTags);
  const { showError, showInfo } = useToast();
  const { isDeleting, remove } = useDeleteEntry();
  const isSaving = useIsPending(requests.saveExpense);
  const isEditMode = Boolean(expense?.id);
  const entryType = isEditMode ? entryTypeOf(expense) : inputType;
  const wording = WORDING[entryType];
  const heading = isEditMode ? wording.editTitle : wording.newTitle;
  const backClose = usePopupBackClose(opened);
  useEnterKeys(backClose.id, {
    "expense-name": { hint: "next", next: "amount" },
    amount: { hint: "done" },
    "spent-at": { hint: "done" },
    [NOTE_INPUT_NAME]: { hint: "enter" },
  });

  const resetForm = useCallback((values) => {
    setInputType(values.type);
    setInputName(values.name);
    setInputAmount(values.amount);
    setPayer(values.payer);
    setInputSplit(values.split);
    setInputTags(values.tags);
    setInputSpentAt(values.spentAt);
    setInputNote(values.note);
    initialSpentAtRef.current = values.spentAt;
    setPayerPopupOpened(false);
    setReceiversPopupOpened(false);
    saveSentRef.current = false;
  }, []);

  // Every group reload brings a new participants array; the effects must not re-run.
  const latest = useRef({});
  latest.current = { expense, participants, opened, me };

  useEffect(() => {
    const current = latest.current;
    resetForm(getInitialValues(expense, current.participants, current.me));
  }, [expense, resetForm]);

  useEffect(() => {
    if (opened && !latest.current.expense?.id) {
      const now = initialSpentAtInput(null);
      setInputSpentAt(now);
      initialSpentAtRef.current = now;
    }
  }, [opened]);

  const participantKey = participants.map((p) => p.id).join(",");
  const meId = me?.id ?? null;
  useEffect(() => {
    const current = latest.current;
    if (!current.opened) {
      resetForm(
        getInitialValues(current.expense, current.participants, current.me),
      );
    }
  }, [participantKey, meId, resetForm]);

  const payerParticipant = participants.find(
    (p) => payer !== "" && Number(p.id) === Number(payer),
  );

  const closePopup = () => popupRef.current?.close();

  const handleSubmit = () => {
    if (saveSentRef.current || isSaving || isDeleting) return;
    const title = inputName.trim();
    const invalid = validate({
      title,
      amount: inputAmount,
      payerParticipant,
      split: inputSplit,
      note: inputNote,
      wording,
    });
    if (invalid) {
      showError(invalid);
      return;
    }
    const { spentAt, error: spentAtError } = resolveSpentAt({
      value: inputSpentAt,
      initialValue: initialSpentAtRef.current,
      isEdit: isEditMode,
    });
    if (spentAtError) {
      showError(spentAtError);
      return;
    }
    const tagsChanged = isEditMode
      ? !sameTags(inputTags, tagNamesOf(expense))
      : inputTags.length > 0;

    const payload = toExpensePayload({
      type: entryType,
      title,
      amount: inputAmount,
      payerId: payerParticipant.id,
      receivers: inputSplit.receivers,
      splitMode: inputSplit.mode,
      spentAt,
      tags: tagsChanged ? inputTags : undefined,
      description: descriptionChange(inputNote, expense),
    });
    const onError = (message) => {
      saveSentRef.current = false;
      showError(message);
    };
    const onSuccess = (group, { queued = false } = {}) => {
      vibrate();
      if (queued) showInfo(queuedText(wording.saved));
      closePopup();
    };
    saveSentRef.current = true;
    dispatch(
      isEditMode
        ? updateExpense(expense.id, payload, onSuccess, onError)
        : createExpense(payload, onSuccess, onError),
    );
  };

  const handleDelete = () => {
    if (!isEditMode || isSaving) return;
    remove(expense, participants, closePopup);
  };

  const handleAmountChange = (cents) => {
    setInputAmount(cents);
    setInputSplit((prev) => resplitForTotal(cents, prev));
  };

  return (
    <Popup
      {...backClose}
      className="expense-popup"
      opened={opened}
      // Not onPopupOpen: F7 fires it before framework7-react binds the handlers.
      onPopupOpened={(popup) => {
        popupRef.current = popup;
        if (autoFocus) nameInputRef.current?.focus();
      }}
      onPopupClosed={() => {
        resetForm(getInitialValues(expense, participants, me));
        onClose();
      }}
    >
      <Page className={`expense-popup-${entryType}`}>
        <PopupNavbar>
          <Icon md="material:edit" ios="f7:pencil" slot="left" />
          <input
            ref={nameInputRef}
            type="text"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            name="expense-name"
            placeholder={wording.namePlaceholder}
            autoCapitalize="sentences"
            autoComplete="off"
            slot="title"
          />
        </PopupNavbar>

        <Block>
          {!isEditMode && (
            <Segmented
              strong
              className="expense-type"
              role="group"
              aria-label="Art"
            >
              {TYPE_OPTIONS.map((option) => (
                <Button
                  key={option.type}
                  small
                  active={entryType === option.type}
                  className={`expense-type-${option.type}`}
                  onClick={() => setInputType(option.type)}
                >
                  {option.label}
                </Button>
              ))}
            </Segmented>
          )}
          {heading && (
            <BlockTitle className="expense-heading">{heading}</BlockTitle>
          )}
          <List strongIos insetIos outlineIos>
            <MoneyInput value={inputAmount} onChange={handleAmountChange} />
          </List>

          <TagPicker
            groupTags={groupTags}
            selected={inputTags}
            onChange={setInputTags}
          />

          <List strong outline inset dividers>
            <ListItem
              className="expense-payer"
              title={wording.payerLabel}
              link
              onClick={() => setPayerPopupOpened(true)}
            >
              {payerParticipant?.name || wording.payerPlaceholder}
            </ListItem>
            <ListItem
              title="Für"
              link
              onClick={() => setReceiversPopupOpened(true)}
            >
              {getReceiversLabel(inputSplit, participants)}
            </ListItem>
          </List>

          <List strongIos insetIos outlineIos className="expense-time">
            <SpentAtInput value={inputSpentAt} onChange={setInputSpentAt} />
            <NoteInput value={inputNote} onChange={setInputNote} />
          </List>
        </Block>
        <Block inset className="expense-actions">
          <Button fill disabled={isSaving || isDeleting} onClick={handleSubmit}>
            Speichern
          </Button>
          {isEditMode && (
            <DeleteButton
              key="delete"
              className="expense-delete-button"
              busy={isDeleting}
              disabled={isSaving}
              onClick={handleDelete}
            >
              {wording.deleteLabel}
            </DeleteButton>
          )}
        </Block>

        {/* Popup rule: one always-rendered wrapper per nested popup – see AGENTS.md. */}
        <div className="payer-popup-wrapper">
          <PersonPickerPopup
            className="payer-popup"
            title={wording.payerPickerTitle}
            opened={payerPopupOpened}
            onClose={() => setPayerPopupOpened(false)}
            participants={participants}
            selectedId={payerParticipant?.id}
            onSelect={(id) => {
              setPayer(id);
              setPayerPopupOpened(false);
            }}
          />
        </div>
        <div className="receivers-popup-wrapper">
          <ReceiversPopup
            title={wording.receiversTitle}
            opened={receiversPopupOpened}
            onClose={() => setReceiversPopupOpened(false)}
            amount={inputAmount}
            split={inputSplit}
            participants={participants}
            updateSplit={setInputSplit}
          />
        </div>
      </Page>
    </Popup>
  );
};

export default ExpensePopup;
